import React, { useState, useEffect } from 'react';
import { 
    CreditCard, 
    Building2, 
    DollarSign, 
    Calendar, 
    CheckCircle, 
    XCircle, 
    AlertTriangle,
    Loader,
    Info
} from 'lucide-react';
import styles from './PaymentAppForm.module.css';
import { 
    getBuildingSubscription, 
    processPayment,
    getBuildingWithSubscription 
} from '../../Fetch/fetchSubscription';

// Subscription pricing function
const getSubscriptionPrice = (numApartments) => {
    console.log('PaymentAppForm - getSubscriptionPrice called with:', numApartments, 'type:', typeof numApartments);
    
    // Convert to number if it's a string
    const apartmentCount = parseInt(numApartments);
    console.log('PaymentAppForm - Converted to number:', apartmentCount);
    
    if (apartmentCount <= 16) {
        console.log('PaymentAppForm - Returning small building price: 50');
        return { price: 50, plan: 'בניין קטן' };
    } else if (apartmentCount <= 80) {
        console.log('PaymentAppForm - Returning medium building price: 70');
        return { price: 70, plan: 'בניין בינוני' };
    } else {
        console.log('PaymentAppForm - Returning large building price: 120');
        return { price: 120, plan: 'בניין גדול' };
    }
};

const PaymentAppForm = ({ buildingId, onPaymentSuccess, onCancel, userRole = "admin" }) => {
    const [subscription, setSubscription] = useState(null);
    const [buildingInfo, setBuildingInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [formData, setFormData] = useState({
        amount: '',
        paymentMethod: 'CreditCard',
        paymentDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        nextPaymentDue: '' // New field for next payment due date
    });
    
    const [formErrors, setFormErrors] = useState({});

    // Payment method options
    const paymentMethods = [
        { value: 'CreditCard', label: 'כרטיס אשראי', icon: <CreditCard size={16} /> },
        { value: 'BankTransfer', label: 'העברה בנקאית', icon: <Building2 size={16} /> },
        { value: 'Cash', label: 'מזומן', icon: <DollarSign size={16} /> },
        { value: 'Check', label: 'צ\'ק', icon: <Calendar size={16} /> }
    ];

    useEffect(() => {
        if (buildingId) {
            loadBuildingData();
        }
    }, [buildingId]);

    const loadBuildingData = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Load both subscription and building info
            const [subscriptionData, buildingData] = await Promise.all([
                getBuildingSubscription(buildingId),
                getBuildingWithSubscription(buildingId)
            ]);

            if (subscriptionData.success) {
                setSubscription(subscriptionData.data);
            } else {
                setError('לא נמצא מנוי עבור בניין זה');
            }

            if (buildingData) {
                console.log('PaymentAppForm - Building data loaded:', buildingData);
                setBuildingInfo(buildingData);
                
                // Get the actual building object from the response
                const building = buildingData.building || buildingData;
                console.log('PaymentAppForm - Building object:', building);
                
                // Use our custom pricing based on apartment count
                if (building && (building.numApartments || building.numberOfApartments)) {
                    const apartmentCount = building.numApartments || building.numberOfApartments;
                    console.log('PaymentAppForm - Found apartment count:', apartmentCount);
                    
                    const { price, plan } = getSubscriptionPrice(apartmentCount);
                    console.log('PaymentAppForm - Setting amount to:', price, 'for plan:', plan);
                    
                    setFormData(prev => ({
                        ...prev,
                        amount: price.toString()
                    }));
                } else {
                    console.log('PaymentAppForm - No apartment count found in building data');
                    console.log('PaymentAppForm - Building structure:', building);
                    // Fallback to subscription monthly price if available
                    if (subscriptionData.success && subscriptionData.data.monthlyPrice) {
                        setFormData(prev => ({
                            ...prev,
                            amount: subscriptionData.data.monthlyPrice.toString()
                        }));
                    }
                }
            }

        } catch (error) {
            setError('שגיאה בטעינת נתוני הבניין: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        // Validate amount
        const amount = parseFloat(formData.amount);
        if (!formData.amount || isNaN(amount) || amount <= 0) {
            errors.amount = 'סכום חייב להיות מספר חיובי';
        } else if (amount > 10000) {
            errors.amount = 'סכום לא יכול להיות יותר מ-10,000 ₪';
        }

        // Validate payment method
        if (!formData.paymentMethod) {
            errors.paymentMethod = 'יש לבחור אמצעי תשלום';
        }

        // Validate payment date
        if (!formData.paymentDate) {
            errors.paymentDate = 'יש לבחור תאריך תשלום';
        } else {
            const selectedDate = new Date(formData.paymentDate);
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            
            if (selectedDate > today) {
                errors.paymentDate = 'תאריך תשלום לא יכול להיות בעתיד';
            }
        }

        // Validate next payment due date
        if (!formData.nextPaymentDue) {
            errors.nextPaymentDue = 'יש לבחור תאריך סיום תשלום';
        } else {
            const nextDueDate = new Date(formData.nextPaymentDue);
            const paymentDate = new Date(formData.paymentDate);
            
            if (nextDueDate <= paymentDate) {
                errors.nextPaymentDue = 'תאריך סיום תשלום חייב להיות אחרי תאריך התשלום';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        if (!subscription) {
            setError('לא נמצא מנוי עבור בניין זה');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const paymentRequest = {
                subscriptionId: subscription.subscriptionId,
                amount: parseFloat(formData.amount),
                paymentMethod: formData.paymentMethod,
                nextPaymentDue: formData.nextPaymentDue // Include the next payment due date
            };


            const result = await processPayment(paymentRequest);

            if (result.success) {
                setSuccess('התשלום עובד בהצלחה!');
                
                // Call success callback if provided
                if (onPaymentSuccess) {
                    setTimeout(() => {
                        onPaymentSuccess(result);
                    }, 2000);
                }
            } else {
                setError(result.message || 'שגיאה בעיבוד התשלום');
            }

        } catch (error) {
            setError('שגיאה בעיבוד התשלום: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear field error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    if (isLoading) {
        return (
            <div className={styles.paymentFormContainer}>
                <div className={styles.loadingState}>
                    <Loader className={styles.spinner} size={32} />
                    <p>טוען נתוני בניין ומנוי...</p>
                </div>
            </div>
        );
    }

    if (error && !subscription) {
        return (
            <div className={styles.paymentFormContainer}>
                <div className={styles.errorState}>
                    <XCircle size={48} className={styles.errorIcon} />
                    <h3>שגיאה בטעינת הנתונים</h3>
                    <p>{error}</p>
                    <div className={styles.errorActions}>
                        <button onClick={loadBuildingData} className={styles.retryButton}>
                            נסה שוב
                        </button>
                        {onCancel && (
                            <button onClick={onCancel} className={styles.cancelButton}>
                                ביטול
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.paymentFormContainer}>
            <div className={styles.formHeader}>
                <div className={styles.headerContent}>
                    <CreditCard size={24} className={styles.headerIcon} />
                    <div>
                        <h2>טופס תשלום חדש</h2>
                        <p>צור תשלום עבור המנוי של הבניין</p>
                    </div>
                </div>
            </div>

            {/* Building and Subscription Info */}
            {(buildingInfo || subscription) && (
                <div className={styles.infoSection}>
                    <div className={styles.infoCard}>
                        <h3>
                            <Building2 size={18} />
                            פרטי בניין ומנוי
                        </h3>
                        <div className={styles.infoGrid}>
                            {buildingInfo && (
                                <>
                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>כתובת:</span>
                                        <span className={styles.infoValue}>
                                            {buildingInfo.address} {buildingInfo.addressNumber}, {buildingInfo.city}
                                        </span>
                                    </div>
                                </>
                            )}
                            {subscription && (
                                <>
                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>סטטוס מנוי:</span>
                                        <span className={`${styles.infoValue} ${styles.statusBadge} ${styles[`status${subscription.status}`]}`}>
                                            {subscription.status}
                                        </span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.infoLabel}>מחיר חודשי:</span>
                                        <span className={styles.infoValue}>₪{subscription.monthlyPrice?.toLocaleString()}</span>
                                    </div>
                                    {subscription.nextPaymentDue && (
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>תשלום הבא:</span>
                                            <span className={styles.infoValue}>
                                                {new Date(subscription.nextPaymentDue).toLocaleDateString('he-IL')}
                                            </span>
                                        </div>
                                    )}
                                    {subscription.isLocked && (
                                        <div className={styles.warningMessage}>
                                            <AlertTriangle size={16} />
                                            <span>המנוי נחסם - תשלום נדרש לפתיחת הגישה</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {success && (
                <div className={styles.successMessage}>
                    <CheckCircle size={20} />
                    <span>{success}</span>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className={styles.errorMessage}>
                    <XCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            {/* Payment Form */}
            <form onSubmit={handleSubmit} className={styles.paymentForm}>
                <div className={styles.formSection}>
                    <h3>פרטי התשלום</h3>
                    
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="amount">
                                <DollarSign size={16} />
                                סכום לתשלום (₪) *
                            </label>
                            <input
                                id="amount"
                                type="number"
                                step="0.01"
                                min="0"
                                max="10000"
                                value={formData.amount}
                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                className={formErrors.amount ? styles.inputError : ''}
                                placeholder="0.00"
                                disabled={isSubmitting}
                            />
                            {formErrors.amount && (
                                <span className={styles.fieldError}>{formErrors.amount}</span>
                            )}
                            {subscription?.monthlyPrice && (
                                <small className={styles.fieldHint}>
                                    מחיר חודשי מומלץ: ₪{subscription.monthlyPrice.toLocaleString()}
                                </small>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="paymentDate">
                                <Calendar size={16} />
                                תאריך תשלום *
                            </label>
                            <input
                                id="paymentDate"
                                type="date"
                                value={formData.paymentDate}
                                onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                                className={formErrors.paymentDate ? styles.inputError : ''}
                                max={new Date().toISOString().split('T')[0]}
                                disabled={isSubmitting}
                            />
                            {formErrors.paymentDate && (
                                <span className={styles.fieldError}>{formErrors.paymentDate}</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="nextPaymentDue">
                                <Calendar size={16} />
                                תאריך סיום תשלום *
                            </label>
                            <input
                                id="nextPaymentDue"
                                type="date"
                                value={formData.nextPaymentDue}
                                onChange={(e) => handleInputChange('nextPaymentDue', e.target.value)}
                                className={formErrors.nextPaymentDue ? styles.inputError : ''}
                                min={formData.paymentDate ? 
                                    new Date(new Date(formData.paymentDate).getTime() + 24 * 60 * 60 * 1000)
                                        .toISOString().split('T')[0] : 
                                    new Date().toISOString().split('T')[0]
                                }
                                disabled={isSubmitting}
                            />
                            {formErrors.nextPaymentDue && (
                                <span className={styles.fieldError}>{formErrors.nextPaymentDue}</span>
                            )}
                            <small className={styles.fieldHint}>
                                תאריך שעד אליו התשלום בתוקף
                            </small>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="paymentMethod">
                            <CreditCard size={16} />
                            אמצעי תשלום *
                        </label>
                        <div className={styles.paymentMethodGrid}>
                            {paymentMethods.map(method => (
                                <label
                                    key={method.value}
                                    className={`${styles.paymentMethodOption} ${
                                        formData.paymentMethod === method.value ? styles.selected : ''
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={method.value}
                                        checked={formData.paymentMethod === method.value}
                                        onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                    <div className={styles.methodContent}>
                                        {method.icon}
                                        <span>{method.label}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                        {formErrors.paymentMethod && (
                            <span className={styles.fieldError}>{formErrors.paymentMethod}</span>
                        )}
                    </div>
                </div>

                {/* Form Actions */}
                <div className={styles.formActions}>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className={styles.cancelActionButton}
                            disabled={isSubmitting}
                        >
                            ביטול
                        </button>
                    )}
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isSubmitting || success}
                    >
                        {isSubmitting ? (
                            <>
                                <Loader className={styles.buttonSpinner} size={16} />
                                מעבד תשלום...
                            </>
                        ) : success ? (
                            <>
                                <CheckCircle size={16} />
                                התשלום בוצע
                            </>
                        ) : (
                            <>
                                <CreditCard size={16} />
                                בצע תשלום
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Additional Info */}
            <div className={styles.additionalInfo}>
                <div className={styles.infoNote}>
                    <Info size={16} />
                    <span>לאחר ביצוע התשלום, המנוי יתעדכן אוטומטית והגישה לבניין תוחזר.</span>
                </div>
            </div>
        </div>
    );
};

export default PaymentAppForm;